import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { KeyValue, Location } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { CallerService } from '../services/caller.service'
import { ChatTimelineItem } from '../utils/chat-timeline.util'
import {
  AppMessages,
  AppMessageValue,
  AppState,
  ChatListingRef,
  ChatMessage,
  ChatOutbox,
  OutgoingChatMessage,
  PublicCorpMember,
} from '../ngrx-store/app.state'
import { MemoizedSelector, Store, createSelector, select } from '@ngrx/store'
import { selectChatOutbox, selectFeatureMessages, selectMessagesWithOutbox } from '../ngrx-store/selectors/corp.selectors'
import {
  newChatMessage,
  newMessage,
  prependOlderMessages,
  updateMessages,
  updateReadReceipts,
} from '../ngrx-store/actions/corp-member.actions'
import { SocketIoChatNamespaceService } from '../services/socket-io.chat-ns.service'
import { HttpStatusCode } from '@angular/common/http'
import { Subject } from 'rxjs'
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators'
import { MentionTextareaComponent } from '../mention-textarea/mention-textarea.component'
import { CreateNewSaleComponent } from '../dialogs/create-new-sale/create-new-sale.component'
import { MatDialog } from '@angular/material/dialog'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { CreateNewAccommodationComponent } from '../dialogs/create-new-accommodation/create-new-accommodation.component'
import { AccommodationChatInfoDialogComponent } from '../dialogs/accommodation-chat-info-dialog/accommodation-chat-info-dialog.component'
import { PublicProfileBottomsheetComponent } from '../public-profile-bottomsheet/public-profile-bottomsheet.component'
import { BookmarkedMessagesBottomsheetComponent } from '../bookmarked-messages-bottomsheet/bookmarked-messages-bottomsheet.component'
import { chatIdentityOf, publicProfileRouteId } from '../utils/corp-member.util'
import { MessageSegment, ParseMentionsPipe } from '../parse-mentions.pipe'
import { queueChatMessage, removeOutboxMessage, retryChatMessage } from '../ngrx-store/actions/chat-outbox.actions'
import { newClientMsgId } from '../utils/chat-outbox.util'
import { ConfirmActionDialogComponent } from '../dialogs/confirm-action-dialog/confirm-action-dialog.component'
import { MESSAGES_CHAT_QUERY_PARAM, NATIONAL_CHAT_ROOM, NATIONAL_CHAT_URL_VALUE } from '../utils/constants.util'

/**
 * TODO: we need a route guard to fetch all the messages;
 * And get the details of the new person they want to chat with.
 */
/** `?about=sale:<slug_id>` -> { listing_type: 'sale', listing_slug }; anything else is null. */
export function parseListingRef(about: string | null): ChatListingRef | null {
  const match = /^(sale|accommodation):([A-Za-z0-9_-]{1,20})$/.exec(about ?? '')
  return match ? { listing_type: match[1] as ChatListingRef['listing_type'], listing_slug: match[2] } : null
}

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessagesComponent implements OnInit, OnDestroy {
  @ViewChild('innerChatBox', { read: ElementRef, static: true })
  innerChatBoxElement!: ElementRef

  @ViewChild('scroller', { read: ElementRef, static: false })
  private scrollContainer!: ElementRef

  @ViewChild(MentionTextareaComponent)
  private mentionTextarea?: MentionTextareaComponent

  state_code: string = this.callerService.corpMember.state_code

  // Sending itself is ChatOutboxEffects' job: this page queues a message and shows the outbox's
  // bubbles (selectMessagesWithOutbox) - which is all a send's outcome changes.
  get isSendingMessage(): boolean {
    return !!this.selectedChatMessages?.some((m) => m.send_status === 'sending')
  }
  // The sender's unsent messages, for Edit (their compose-box draft) - by client_msg_id.
  private outbox: ChatOutbox = {}

  // onEvent() multicasts per event name (see SocketIoChatNamespaceService) — takeUntil(destroy$)
  // is enough to clean up this component's subscription without affecting any other consumer's.
  private destroy$ = new Subject<void>()

  // Stateless pure transform with no injected deps of its own — safe to instantiate directly
  // rather than adding it to a provider list just for this one non-template usage.
  private parseMentionsPipe = new ParseMentionsPipe()
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private socketIoChatNamespaceService: SocketIoChatNamespaceService,
    public store: Store<AppState>,
    public callerService: CallerService,
    private cdf: ChangeDetectorRef,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private location: Location,
  ) {}

  // True when the chat socket's transport is down. Drives the offline banner and disables
  // the send button - see the connected$ subscription in ngOnInit for why this no longer
  // also depends on OnlineStatusService.
  isOffline = false

  /** The logged-in corp member's chat identity. Chats identify participants
   *  (message_from/message_to/recipient_id/initiator_id) by this value. */
  get myId(): string | undefined {
    return chatIdentityOf(this.callerService.corpMember)
  }

  appStateChats: AppMessages = new Map()

  // Exposed for the template.
  readonly NATIONAL_CHAT_ROOM = NATIONAL_CHAT_ROOM

  selectedChatMessages: Array<ChatMessage> | undefined = undefined

  // Is a one item array of the selected chat's room. Mirrors the ?chat= query param, which is
  // the source of truth - see the queryParamMap subscription in ngOnInit.
  selectedChat: FormControl<Array<string> | null> = new FormControl(null)

  // True while the history entry below the current one is the chat list we pushed the open
  // chat on top of, so closing the chat can go back instead of stacking another list entry.
  private openedChatFromList = false
  chatMessage = new FormControl({ value: '', disabled: false })


  // Track if we should show notification for accommodation chat
  showAccommodationNotification = false

  // WhatsApp-style reply-to-message: the message currently quoted in the compose bar, if any.
  replyingTo: ChatMessage | null = null
  /**
   * The listing a chat was just started from ("Chat seller" / "Chat", `?about=sale:<slug_id>`): the next
   * message sent in that room is tagged with it, so the seller's delete warning can find the chat.
   */
  private draftListing: (ChatListingRef & { room: string }) | null = null
  // Briefly highlighted after scrollToMessage() locates its target.
  highlightedMessageId: number | string | null = null
  bookmarkedMessageIds = new Set<number>()

  // Older-message pagination, keyed by room. `false` means the room's start has been reached.
  private isLoadingOlderMessages = new Map<string, boolean>()
  private hasMoreOlderMessages = new Map<string, boolean>()

  // Derived from selectedChat + appStateChats, recomputed by updateSelectedChatDisplayInfo()
  // whenever either changes — rather than on every template read/CD pass (see #3 review notes).
  selectedChatRecipientName: string | undefined = undefined
  selectedChatRecipientStateCode: string | undefined = undefined

  // MemorizedSelector
  selectMessageChats = createSelector(
    selectMessagesWithOutbox,
    (messagesState: AppMessages) =>
      messagesState.get?.(this.selectedChat.value?.[0])?.texts
  )

  ngOnInit() {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(({ res }) => {
      // MessagesResolver emits null when loading fails (e.g. offline) so the page still opens.
      if (res?.status === HttpStatusCode.Ok) {
        // updating, not initialzing - cause initializing gets rid of newly started chats
        this.store.dispatch(updateMessages(res.body.results))
      }
    })

    this.callerService.getBookmarkedChatMessages().subscribe({
      next: (res: any) => {
        const bookmarked = res.body?.data ?? []
        this.bookmarkedMessageIds = new Set(bookmarked.map((chat: ChatMessage) => chat.id as number))
      },
    })

    // https://stackoverflow.com/questions/55138631/looking-for-an-angular-7-version-of-vue-js-nexttick
    // https://www.digitalocean.com/community/tutorials/angular-change-detection-strategy

    this.store.pipe(select(selectChatOutbox), takeUntil(this.destroy$)).subscribe((outbox) => {
      this.outbox = outbox ?? {}
    })

    this.store
      .pipe(select(selectMessagesWithOutbox), takeUntil(this.destroy$))
      .subscribe({
        next: (value: AppMessages) => {
          // When a phantom room (FE-generated UUID) is replaced by the canonical server
          // room after updateMessages fires, re-select the replacement automatically.
          const currentRoom = this.selectedChat.value?.[0]
          if (currentRoom && currentRoom !== NATIONAL_CHAT_ROOM && !value.has(currentRoom)) {
            const prevChat = this.appStateChats.get(currentRoom)
            if (prevChat) {
              const myId = this.myId
              const otherId = prevChat.recipient_id === myId ? prevChat.initiator_id : prevChat.recipient_id
              const replacementRoom = Array.from(value.keys()).find(room => {
                if (room === NATIONAL_CHAT_ROOM) return false
                const chat = value.get(room)
                return chat &&
                  (chat.recipient_id === otherId || chat.initiator_id === otherId) &&
                  (chat.recipient_id === myId || chat.initiator_id === myId)
              })
              if (replacementRoom) {
                // Store the new chats first: selecting reads the room from appStateChats (its
                // ?chat= URL sync and message loading) and wouldn't find the replacement yet.
                this.appStateChats = value
                this.selectedChat.setValue([replacementRoom as string])
              }
            }
          }

          this.appStateChats = value
          this.updateSelectedChatDisplayInfo()
          this.cdf.detectChanges()

          // If a new message just arrived for the room the user currently has open, mark it
          // read immediately instead of waiting for them to switch away and back. This used to
          // live in the CHAT_MESSAGE socket handler directly; it now reacts to the store instead,
          // since CHAT_MESSAGE is dispatched globally from NavComponent, not locally here.
          // Applies to national-chats too — the backend's markRoomChatsAsRead already special-cases
          // that room (bumping last_seen_national_chat_at instead of per-message time_read), so
          // there's no reason to exclude it here.
          const openRoom = this.selectedChat.value?.[0]
          if (openRoom && value.get(openRoom)?.unread_messages) {
            this.store.dispatch(updateReadReceipts({ room: openRoom }))
            this.callerService.updateRoomReadReceipts({ room: openRoom }).subscribe()
          }
        },
      })

    // Resolve "chat with this person" links (?r=<recipient id>) to their room.
    // Using queryParamMap (not route.snapshot) means this also fires when the user
    // is already on the messages page and starts a new chat from another component.
    // The outer filter blocks emissions where r is absent — including the ?chat= URL this
    // swaps in — so the pipeline only runs for real chat links.
    this.route.queryParamMap.pipe(
      filter(params => !!params.get('r')),
      switchMap(params => {
        const recipientId = params.get('r')
        const from = params.get('from')
        const draft = params.get('draft')
        const about = parseListingRef(params.get('about'))

        // switchMap into the store so we wait for the matching room to be present
        // before selecting it, handling any timing gap between the action and resolver.
        return this.store.pipe(
          select(selectFeatureMessages),
          map(chats => this.findRoomWith(chats, recipientId)),
          filter((chatRoom): chatRoom is string => !!chatRoom),
          take(1),
          map(chatRoom => ({ chatRoom, from, draft, about }))
        )
      }),
      takeUntil(this.destroy$)
    ).subscribe(({ chatRoom, from, draft, about }) => {
      if (from === 'accommodation') {
        this.showAccommodationNotification = true
        this.cdf.detectChanges()
      }

      // "Chat seller" types a first message about the item for them - never over what they'd typed.
      if (draft && !this.chatMessage.value) {
        this.chatMessage.setValue(draft)
      }
      this.draftListing = about ? { ...about, room: chatRoom } : null

      // Swap the ?r= link for the chat's own URL; the ?chat= subscription below opens it.
      this.navigateToChat(this.chatParamForRoom(chatRoom, this.appStateChats), { replaceUrl: true })
    })

    /**
     * @todo: (major refactor) we shouldn't be receiving the whole chat data. We should be receiving the new messages only.
     */
    this.store.pipe(select(this.selectMessageChats), takeUntil(this.destroy$)).subscribe({
      next: (value: Array<ChatMessage> | undefined) => {
        if (Array.isArray(value)) {
          // Decide before re-rendering: once the DOM grows, "was the reader at the bottom?"
          // can no longer be measured.
          const shouldFollow = this.shouldFollowNewMessages(this.selectedChatMessages, value)

          this.selectedChatMessages = [...value]

          this.cdf.detectChanges()

          if (shouldFollow) this.scrollToBottomOfChatBox()
        }
      },
    })

    // TODO: if no previous chat.
    // TODO: fetch the chat details. (get the new chat name from the sale post. Pick it from the state.) so there's no extra call.
    this.selectedChat?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      // @todo: if there are unread messages, clear the unread count.

      // Selections made in the page (chat list, bookmarks) land here first; push them to the URL.
      // When the URL itself triggered this - or a phantom room was swapped for its canonical one,
      // same participants - the params already match.
      const chatParam = this.chatParamForRoom(value?.[0] ?? null, this.appStateChats)
      if (chatParam !== this.route.snapshot.queryParamMap.get(MESSAGES_CHAT_QUERY_PARAM)) {
        this.syncChatToUrl(chatParam)
      }

      this.updateSelectedChatDisplayInfo()

      if (value?.[0] && this.appStateChats.has(value?.[0])) {
        this.selectedChatMessages = this.appStateChats.get?.(value?.[0])?.texts

        // weirdly, they both have to be called for the scroll to work.
        this.cdf.detectChanges()
        this.cdf.markForCheck()

        // scroll after view is done rendering or we're done showing all the messages.
        this.scrollToBottomOfChatBox('instant')
        
        // If notification should be shown, ensure it's visible after a brief delay
        if (this.showAccommodationNotification) {
          setTimeout(() => {
            this.cdf.detectChanges()
            this.cdf.markForCheck()
          }, 100)
        }

        /**
         * update the messages are read.
         * send be request, and on success, update local state
         *
         * Only send request if there's unread messages
         */
        if (this.appStateChats.get?.(value?.[0])?.unread_messages) {
          this.callerService
            .updateRoomReadReceipts({ room: value?.[0] })
            .subscribe({
              next: () => {
                // dispatch read receipts in local state
                this.store.dispatch(updateReadReceipts({ room: value?.[0] }))
              },
              error: (err) => {
                console.error('Could not update read receipts', err)
              },
            })
        }
      } else {
        this.selectedChatMessages = undefined
      }
    })

    // The URL is the source of truth for the open chat: tapping the Chats nav link (plain
    // /messages), browser/phone back and reloads all flow through here. Registered after the
    // valueChanges subscription so the initial emission loads that room's messages.
    this.route.queryParamMap.pipe(
      map(params => params.get(MESSAGES_CHAT_QUERY_PARAM)),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((chatParam) => {
      if (!chatParam) {
        this.openedChatFromList = false
      }

      // A stale link, a reload on a not-yet-saved new chat, or someone you have no chat with -
      // fall back to the list.
      const room = this.roomForChatParam(chatParam, this.appStateChats)
      if (chatParam && !room) {
        this.navigateToChat(null, { replaceUrl: true })
        return
      }

      if (room !== (this.selectedChat.value?.[0] ?? null)) {
        this.selectedChat.setValue(room ? [room] : null)
        this.cdf.markForCheck()
      }
    })

    // NOTE: CHAT_MESSAGE is no longer listened to here — SocketEffects dispatches
    // newChatMessage globally so incoming messages update the store regardless of which
    // page is active. The "mark as read if this room is currently open" behavior that used
    // to live in this listener now lives in the selectFeatureMessages subscription above.

    // Connectivity indicator: true when the chat socket's transport is down. Drives the
    // offline banner and disables the send button.
    //
    // This used to also OR in OnlineStatusService.isOnline$ (navigator.onLine + the browser's
    // online/offline events), on the theory that it'd flag a dead network faster than the
    // socket's own ping-timeout-based detection. In practice that made things worse: the
    // browser's 'online' event is well known to not reliably fire on reconnect on some mobile
    // browsers, so once isOnline$ got stuck at false, the banner (and disabled send button)
    // never cleared even after the socket successfully reconnected and proved the connection
    // was fine. socketConnected is already the more authoritative signal - a real round-trip
    // to our own backend - so it doesn't need a flakier proxy backing it up: any real network
    // loss shows up here too, just gated by the backend's deliberately generous ping timeout
    // (see the pingTimeout/pingInterval comment in sockets/routes.js on the backend) so brief
    // mobile blips don't flap the indicator either.
    this.socketIoChatNamespaceService.connected$.pipe(takeUntil(this.destroy$)).subscribe((socketConnected) => {
      this.isOffline = !socketConnected
      this.cdf.detectChanges()
    })

    // Resync on reconnect (not the initial connect, which the route resolver already loads
    // fresh data for): refetch chat data the same way the resolver does, so anything sent
    // while this tab was disconnected shows up without requiring a manual page reload.
    this.socketIoChatNamespaceService.reconnected$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.callerService.getAllChats().subscribe({
        next: (res: any) => {
          if (res.status === HttpStatusCode.Ok) {
            this.store.dispatch(updateMessages(res.body.results))
          }
        },
      })
    })
  }

  /**
   * Mirrors an in-page selection into the URL. Opening a chat from the list pushes a history
   * entry, so back closes it; switching chats replaces it; closing goes back to that list entry.
   */
  private syncChatToUrl(chatParam: string | null): void {
    const hadChat = !!this.route.snapshot.queryParamMap.get(MESSAGES_CHAT_QUERY_PARAM)

    if (!chatParam && this.openedChatFromList) {
      this.openedChatFromList = false
      this.location.back()
      return
    }

    if (chatParam && !hadChat) {
      this.openedChatFromList = true
    }
    this.navigateToChat(chatParam, { replaceUrl: hadChat })
  }

  private navigateToChat(chatParam: string | null, { replaceUrl }: { replaceUrl: boolean }): void {
    // Empty commands keep the /messages path; a null param drops ?chat= (and ?r=/?from=/?i=).
    this.router.navigate([], {
      queryParams: { [MESSAGES_CHAT_QUERY_PARAM]: chatParam },
      replaceUrl,
    })
  }

  /** The DM room between the logged-in member and `otherId`, if there is one. */
  private findRoomWith(chats: AppMessages, otherId: string | null): string | undefined {
    const myId = this.myId
    return Array.from(chats.keys()).find((room) => {
      if (room === NATIONAL_CHAT_ROOM) return false
      const chat = chats.get(room)
      return (
        (chat?.recipient_id === otherId && chat?.initiator_id === myId) ||
        (chat?.recipient_id === myId && chat?.initiator_id === otherId)
      )
    }) as string | undefined // room keys are always strings in practice
  }

  /** Room id -> ?chat= value: the national chat by name, a DM by the other person's slug_id. */
  private chatParamForRoom(room: string | null, chats: AppMessages): string | null {
    if (!room) return null
    if (room === NATIONAL_CHAT_ROOM) return NATIONAL_CHAT_URL_VALUE
    const chat = chats.get(room)
    if (!chat) return null
    return (chat.initiator_id === this.myId ? chat.recipient_id : chat.initiator_id) ?? null
  }

  /** ?chat= value -> room id, or null when there's no such chat. */
  private roomForChatParam(chatParam: string | null, chats: AppMessages): string | null {
    if (!chatParam) return null
    if (chatParam === NATIONAL_CHAT_URL_VALUE) return NATIONAL_CHAT_ROOM
    return this.findRoomWith(chats, chatParam) ?? null
  }

  dismissAccommodationNotification(): void {
    this.showAccommodationNotification = false
    this.cdf.detectChanges()
  }

  trackByTimelineItem(_index: number, item: ChatTimelineItem): number | string {
    return item.message.id
  }

  trackByChatEntry(_index: number, entry: KeyValue<string | number | undefined, AppMessageValue>): string | number | undefined {
    return entry.key
  }

  /**
   * The other participant in a 1:1 chat. Prefers deriving it from the first message (richer —
   * carries `Media` for a real photo). A brand-new chat has no messages yet, so falls back to
   * the chat's own recipient/initiator name fields instead — without this, `entity` would be
   * `undefined` and the avatar would show a bare '?' until the first message is sent, instead of
   * initials.
   */
  getOtherParticipant(chatValue: AppMessageValue | undefined): PublicCorpMember | undefined {
    const firstMessage = chatValue?.texts?.[0]
    if (firstMessage) {
      return firstMessage.message_from === this.myId
        ? firstMessage.ToCorpMember
        : firstMessage.FromCorpMember
    }

    if (!chatValue) return undefined
    const isInitiator = chatValue.initiator_id === this.myId
    const name = isInitiator ? chatValue.recipient_name : chatValue.initiator_name
    if (!name) return undefined

    return {
      // No numeric id available for a brand-new chat — only its slug_id (carried as
      // recipient_id/initiator_id). publicProfileRouteId() prefers slug_id, so this is enough
      // to open the profile even though `id` itself is a placeholder.
      id: 0,
      first_name: name,
      nickname: undefined,
      state_code: (isInitiator ? chatValue.recipient_state_code : chatValue.initiator_state_code) ?? '',
      slug_id: isInitiator ? chatValue.recipient_id : chatValue.initiator_id,
      public_profile: undefined,
      created_at: '',
      Media: undefined,
    }
  }

  /** Plain-text rendering of a message for chat-list previews — @mention tokens become "@Name"
   *  instead of the raw @[[slug_id:Name]] wire format. Mirrors the same snippet() used in
   *  BookmarkedMessagesBottomsheetComponent. */
  snippet(message: string | undefined): string {
    if (!message) return ''
    return this.parseMentionsPipe
      .transform(message)
      .map((segment: MessageSegment) => (segment.type === 'mention' ? '@' + segment.value : segment.value))
      .join('')
  }

  openPublicProfile(member: PublicCorpMember | undefined): void {
    const corperSlug = publicProfileRouteId(member)
    if (!corperSlug) return

    this.bottomSheet.open(PublicProfileBottomsheetComponent, {
      data: { corperSlug },
    })
  }

  openMentionProfile(segment: MessageSegment): void {
    if (segment.type !== 'mention' || !segment.id) return

    this.bottomSheet.open(PublicProfileBottomsheetComponent, {
      data: { corperSlug: String(segment.id) },
    })
  }

  /**
   * Links inside chat messages are user-generated content, so never navigate straight to them —
   * warn that the user is about to leave corpers.ng for an external site and let them back out.
   */
  confirmOpenLink(url: string | undefined): void {
    if (!url) return

    this.dialog
      .open(ConfirmActionDialogComponent, {
        width: '420px',
        data: {
          title: 'Leave corpers.ng?',
          message: 'This link will open in a new browser tab. Only continue if you trust where it leads.',
          highlight: url,
          confirmText: 'Open link',
          cancelText: 'Cancel',
          confirmColor: 'primary',
        },
        ariaLabel: 'Confirm opening external link',
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirmed) {
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      })
  }

  setReplyTarget(message: ChatMessage): void {
    // Only ever reply to a server-confirmed message — a pending optimistic message's id
    // never reaches the backend, so there'd be nothing real to reference.
    if (message.pending) return
    this.replyingTo = message
    this.mentionTextarea?.focus()
  }

  cancelReply(): void {
    this.replyingTo = null
  }

  toggleBookmark(message: ChatMessage): void {
    if (message.pending || typeof message.id !== 'number') return
    const id = message.id

    if (this.bookmarkedMessageIds.has(id)) {
      this.bookmarkedMessageIds.delete(id)
      this.callerService.unbookmarkChatMessage(id).subscribe({
        error: () => {
          // Roll back the optimistic UI update on failure.
          this.bookmarkedMessageIds.add(id)
        },
      })
    } else {
      this.bookmarkedMessageIds.add(id)
      this.callerService.bookmarkChatMessage(id).subscribe({
        error: () => {
          this.bookmarkedMessageIds.delete(id)
        },
      })
    }
  }

  openBookmarkedMessages(): void {
    this.bottomSheet.open(BookmarkedMessagesBottomsheetComponent).afterDismissed().subscribe((room?: string) => {
      if (room) {
        this.selectedChat.setValue([room])
      }
    })
  }

  private static readonly OLDER_MESSAGES_PAGE_SIZE = 50
  private static readonly SCROLL_TOP_LOAD_THRESHOLD_PX = 50
  // Within this distance of the bottom, the reader counts as "caught up" and new messages scroll into view.
  private static readonly FOLLOW_NEW_MESSAGES_THRESHOLD_PX = 120

  onChatScroll(): void {
    const el = this.scrollContainer?.nativeElement
    const room = this.selectedChat.value?.[0]
    if (!el || !room || el.scrollTop > MessagesComponent.SCROLL_TOP_LOAD_THRESHOLD_PX) return
    this.loadOlderMessages(room)
  }

  loadOlderMessages(room: string, onComplete?: () => void): void {
    if (this.isLoadingOlderMessages.get(room)) return
    if (this.hasMoreOlderMessages.get(room) === false) {
      onComplete?.()
      return
    }

    const texts = this.appStateChats.get(room)?.texts ?? []
    const numericIds = texts.map((t) => t.id).filter((id): id is number => typeof id === 'number')
    if (!numericIds.length) {
      onComplete?.()
      return
    }
    const oldestId = Math.min(...numericIds)

    this.isLoadingOlderMessages.set(room, true)
    const el = this.scrollContainer?.nativeElement
    const previousScrollHeight = el?.scrollHeight ?? 0

    this.callerService.getOlderChatMessages(room, oldestId, MessagesComponent.OLDER_MESSAGES_PAGE_SIZE).subscribe({
      next: (res: any) => {
        const messages: ChatMessage[] = res.body?.data ?? []
        this.hasMoreOlderMessages.set(room, messages.length >= MessagesComponent.OLDER_MESSAGES_PAGE_SIZE)
        if (messages.length) {
          this.store.dispatch(prependOlderMessages({ room, messages }))
        }
        this.isLoadingOlderMessages.set(room, false)

        // Preserve scroll position so prepending older messages doesn't jump the viewport —
        // wait a tick for the DOM to actually grow before measuring the new scrollHeight.
        setTimeout(() => {
          if (el) {
            el.scrollTop = el.scrollHeight - previousScrollHeight
          }
          onComplete?.()
        }, 0)
      },
      error: () => {
        this.isLoadingOlderMessages.set(room, false)
        onComplete?.()
      },
    })
  }

  private highlightMessage(id: number | string): void {
    this.highlightedMessageId = id
    this.cdf.detectChanges()
    document.getElementById('msg-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => {
      this.highlightedMessageId = null
      this.cdf.detectChanges()
    }, 1500)
  }

  scrollToMessage(id: number, attemptsLeft = 5): void {
    const alreadyLoaded = this.selectedChatMessages?.some((m) => m.id === id)
    if (alreadyLoaded) {
      this.highlightMessage(id)
      return
    }

    const room = this.selectedChat.value?.[0]
    if (!room || attemptsLeft <= 0 || this.hasMoreOlderMessages.get(room) === false) {
      this.callerService.showNotification('Could not find that message.')
      return
    }

    this.loadOlderMessages(room, () => this.scrollToMessage(id, attemptsLeft - 1))
  }

  openAccommodationChatInfoDialog(): void {
    this.dialog.open(AccommodationChatInfoDialogComponent, {
      maxWidth: '500px',
      width: '90%',
      data: {},
      ariaLabel: 'Information about accommodation chat',
    }).afterClosed().subscribe(() => {
      this.dismissAccommodationNotification()
    })
  }

  /**
   * Only follow the conversation down when a new message lands at the end AND it's either ours
   * or the reader was already at the bottom. Prepending older messages, read-receipt/pending
   * updates, or someone else posting while you read history must not yank the viewport.
   */
  private shouldFollowNewMessages(previous: ChatMessage[] | undefined, next: ChatMessage[]): boolean {
    const newLast = next.at(-1)
    if (!newLast || newLast.id === previous?.at(-1)?.id) return false
    if (newLast.message_from === this.myId) return true

    const el: HTMLElement | undefined = this.scrollContainer?.nativeElement
    if (!el) return true
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    return distanceFromBottom <= MessagesComponent.FOLLOW_NEW_MESSAGES_THRESHOLD_PX
  }

  // https://stackoverflow.com/a/45367387/9259701
  scrollToBottomOfChatBox(
    scrollBehavior: 'smooth' | 'instant' | 'auto' = 'smooth'
  ) {
    // todo: only scroll when they're at the bottom of the page.

    if (this.scrollContainer?.nativeElement) {
      setTimeout(() => {
        this.scrollContainer?.nativeElement?.scrollTo?.({
          behavior: scrollBehavior,
          top: this.scrollContainer?.nativeElement?.scrollHeight,
        })
      }, 0)
    }
  }

  sendMessage() {
    // Spaces and newlines around a message would show as blank lines in its bubble.
    if (!this.chatMessage.value?.trim()) {
      this.callerService.showNotification('Please enter a message')
      return
    }
    const myId = this.myId
    if (!myId) {
      this.callerService.showNotification("Couldn't identify you. Please try logging in again.")
      return
    }

    // The textarea shows friendly "@Name" text; the wire/storage format (and what the
    // parseMentions render pipe expects) is "@[[id:Name]]". Build that once here, up front,
    // before anything clears the visible draft.
    const wireMessage = (this.mentionTextarea?.buildWireMessage() ?? this.chatMessage.value ?? '').trim()
    // Captured before the draft gets cleared further down in this same call — kept with the
    // unsent message so Edit can restore the compose box with its mention formatting intact.
    const draftSnapshot = this.mentionTextarea?.captureDraft() ?? { text: this.chatMessage.value ?? '', mentions: [] }

    const selectedRoom = this.selectedChat.value?.[0]
    // Reply targets are only ever set on already-confirmed messages (see setReplyTarget()), so
    // `id` is always a real numeric Chat id here, never the optimistic "pending-<timestamp>" string.
    const replyToChatId = typeof this.replyingTo?.id === 'number' ? this.replyingTo.id : undefined
    const replyToPreview = this.replyingTo

    let msg: OutgoingChatMessage | null = null
    // Kept across retries of this message, so the backend can tell a resend from a new message.
    const clientMsgId = newClientMsgId()

    if (selectedRoom === NATIONAL_CHAT_ROOM) {
      // send to national chat
      msg = {
        client_msg_id: clientMsgId,
        message: wireMessage,
        room: NATIONAL_CHAT_ROOM,
        message_from: myId,
        reply_to_chat_id: replyToChatId,
      }
    } else {
      // If it's a new chat, we take recipient_id, else ...
      let messageTo =
        this.appStateChats.get(selectedRoom)?.recipient_id !== myId
          ? this.appStateChats.get(selectedRoom)?.recipient_id
          : this.appStateChats.get(selectedRoom)?.initiator_id

      // if it's not a new chat
      if (!messageTo) {
        const _chat = this.appStateChats.get(selectedRoom)?.texts?.[0]
        const other = _chat?.message_from === myId ? _chat?.ToCorpMember : _chat?.FromCorpMember
        messageTo = chatIdentityOf(other)
      }

      msg = {
        client_msg_id: clientMsgId,
        message: wireMessage,
        message_to: messageTo,
        message_from: myId,
        room: selectedRoom ?? this.appStateChats.get(selectedRoom)?.room, // window.crypto.randomUUID(), // https://stackoverflow.com/a/27747377/9259701
        reply_to_chat_id: replyToChatId,
      }
      if (this.draftListing && this.draftListing.room === msg.room) {
        msg.listing_type = this.draftListing.listing_type
        msg.listing_slug = this.draftListing.listing_slug
      }
    }

    // Then clear the input after sending message.
    // Use web sockets...

    if (msg) {
      const isNationalChat = msg.room === NATIONAL_CHAT_ROOM
      if (!isNationalChat && !msg.message_to) {
        this.callerService.showNotification("Couldn't identify the recipient. Please try again.")
        return
      }

      // Shown straight away; ChatOutboxEffects sends it (or keeps it until it can).
      const pendingMsg: ChatMessage = {
        id: 'pending-' + clientMsgId,
        client_msg_id: clientMsgId,
        message: msg.message,
        message_from: msg.message_from,
        message_to: msg.message_to ?? '',
        room: msg.room ?? '',
        pending: true,
        send_status: 'sending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        age: '',
        message_sent: false,
        read_by_to: false,
        _time_read: '',
        FromCorpMember: {
          id: this.callerService.corpMember.id,
          first_name: this.callerService.corpMember.first_name,
          state_code: this.callerService.corpMember.state_code,
          nickname: this.callerService.corpMember.nickname,
          slug_id: this.callerService.corpMember.slug_id,
          public_profile: this.callerService.corpMember.public_profile,
          created_at: this.callerService.corpMember.created_at,
          Media: this.callerService.corpMember.Media,
        },
        ToCorpMember: { id: 0, slug_id: msg.message_to, first_name: '', state_code: '', nickname: undefined, created_at: '' },
        reply_to_chat_id: replyToChatId,
        ReplyTo: replyToPreview
          ? { id: replyToPreview.id as number, message: replyToPreview.message, message_from: replyToPreview.message_from, FromCorpMember: replyToPreview.FromCorpMember }
          : null,
      }
      this.store.dispatch(queueChatMessage({
        entry: { message: pendingMsg, outgoing: msg, draft: draftSnapshot, status: 'sending', autoRetry: true },
      }))
      // Only the first message is about the listing - it carries it through any retries (and
      // Edit hands it back).
      if (msg.listing_slug) this.draftListing = null
      this.chatMessage.setValue('')
      this.replyingTo = null
      this.cdf.detectChanges()
    }
  }

  /** "Not sent - tap to retry": resends the message as it was, under the same client_msg_id. */
  retryMessage(message: ChatMessage): void {
    if (message.send_status !== 'failed' || !message.client_msg_id) return
    this.store.dispatch(retryChatMessage({ client_msg_id: message.client_msg_id }))
  }

  /** Moves a message that isn't sent back into the compose box (with its @mentions), to change it. */
  editUnsentMessage(message: ChatMessage): void {
    const entry = message.client_msg_id ? this.outbox[message.client_msg_id] : undefined
    if (!entry || entry.status === 'sending') return
    this.store.dispatch(removeOutboxMessage({ client_msg_id: entry.outgoing.client_msg_id }))
    this.mentionTextarea?.restoreDraft(entry.draft)
    this.replyingTo = this.selectedChatMessages?.find((m) => m.id === entry.outgoing.reply_to_chat_id) ?? null
    const { listing_type, listing_slug } = entry.outgoing
    if (listing_type && listing_slug && entry.outgoing.room) {
      this.draftListing = { listing_type, listing_slug, room: entry.outgoing.room }
    }
    this.mentionTextarea?.focus()
    this.cdf.detectChanges()
  }

  deleteUnsentMessage(message: ChatMessage): void {
    if (!message.client_msg_id || message.send_status === 'sending') return
    this.store.dispatch(removeOutboxMessage({ client_msg_id: message.client_msg_id }))
  }

  closeCurrentChat() {
    this.selectedChat.reset(undefined)
  }

  openCreateSalePostDialog() {
    this.dialog.open(CreateNewSaleComponent, {
      maxWidth: '100dvw',
      maxHeight: '100dvh',
      height: '100%',
      width: '100%',
      data: {},
      ariaLabel: 'Dialog to create a new sale post',
      role: 'dialog',
    })

  }

  openCreateAccommodationPostDialog() {
    this.dialog.open(CreateNewAccommodationComponent, {
      maxWidth: '100dvw',
      maxHeight: '100dvh',
      height: '100%',
      width: '100%',
      data: {},
      ariaLabel: 'Dialog to create a new accommodation post',
      role: 'dialog',
    })

  }

  /** Recomputes selectedChatRecipientName/StateCode. Call whenever selectedChat or the
   *  current room's entry in appStateChats changes — not from the template, so this runs
   *  only when the underlying data actually changes rather than on every CD pass. */
  private updateSelectedChatDisplayInfo(): void {
    const room = this.selectedChat.value?.[0]
    if (room === NATIONAL_CHAT_ROOM) {
      this.selectedChatRecipientName = "Corpers' Chat"
      this.selectedChatRecipientStateCode = '36 States'
      return
    }

    const chat = room ? this.appStateChats.get(room) : undefined
    const isInitiator = !!chat && chat.initiator_id === this.myId
    this.selectedChatRecipientName = isInitiator ? chat?.recipient_name : chat?.initiator_name
    this.selectedChatRecipientStateCode = isInitiator
      ? chat?.recipient_state_code
      : chat?.initiator_state_code
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
